import React, { Fragment, useEffect, useState, Suspense, lazy } from "react";
import { bool, number, string } from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import get from "lodash/get";
import { Link, SocialShare } from "@quintype/components";

import { AppLogo, Button, HeaderMenuItem, Headline } from "../../../atoms";
import { BUTTONS_DATA, STATIC_KEYWORDS } from "../../../../constants";
import { MEMBER_UPDATED, OPEN_HAMBURGER_MENU } from "../../../store/actions";
import { MenuWithDropdown } from "../../../molecules";
import { NavbarSearch } from "../navbar-search";
import { useSummaryCheck } from "../../../customHooks/useSummaryCheck";
import HamburgerMenu from "../../../atoms/hamburger-menu";
import { SocialShareTemplate } from "../../../../arrow";
import { useDeviceType } from "../../../customHooks/useDeviceType";
import AuthButton from "../../../atoms/AuthButton";
import {
  addDatalayerEventCollection,
  getAiSummaryEligibility,
  getCookie,
  getCreatorSocialTitle,
} from "../../../utils";
import { getCategoryAndSubCategory, getTemplateName } from "../../../../ga-utils";
import ToastMessage from "../toastMessage";
// import { RightArrow } from "../../../../arrow/components/Svgs/right-arrow";
import OneTapLogin from "../../../atoms/OneTapLogin";

import CompanyIcon from "../../../atoms/CompanyIcon";
import { UserFallbackIcon } from "../../../../arrow/components/Svgs/user-fallback-icon";
import { getAiFaqStoryId, useAiFaqData } from "../../../customHooks/useAiFaqData";
import "./top-bar.m.css";
import { EntityListingStory } from "../../../atoms/EntityListingStory";
import { getStoryUrl } from "../../../../arrow/utils/utils";

const SummaryCard = lazy(() => import("../../../molecules/SummaryCard"));

const getStoryContentId = (story = {}) => story["story-content-id"] || story.id;
const STORY_TOP_MENU_THRESHOLD = 120;

const TopBar = ({ showOneTap, headline, publishTime }) => {
  const [toastMessage, setToastMessage] = useState("");
  const [memberUpdated, setMemberUpdated] = useState(false);
  const hasLockedBodyScrollRef = React.useRef(false);
  // const [loadOneTap, setGoogleOneTap] = useState(false);
  const isMobile = useDeviceType() !== "desktop";
  const dispatch = useDispatch();
  const { SIGN_IN, GO_AD_FREE } = STATIC_KEYWORDS;
  const isAdfree = useSelector((state) => get(state, ["qt", "config", "subscriber"], false));
  const enableSubscription = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "enable_subscription"], false)
  );

  const member = useSelector((state) => get(state, ["member"], null));
  const isHamburgerMenuOpen = useSelector((state) => get(state, ["isHamburgerMenuOpen"], false));
  const hamburgerMenu = useSelector((state) => get(state, ["qt", "data", "navigationMenu", "hamburgerMenu"], []));
  // const GoogleClientId = useSelector((state) =>
  //   get(state, ["qt", "config", "publisher-attributes", "google_client_id"], "")
  // );
  const [logoClick, setLogoClick] = useState(false);

  const displayOverlay = isHamburgerMenuOpen ? ".75" : "0";
  const toggleHandler = () => {
    dispatch({
      type: OPEN_HAMBURGER_MENU,
      isHamburgerMenuOpen: !isHamburgerMenuOpen,
    });
  };

  const setMember = (member) => {
    console.log("one tap login member updated");
    dispatch({ type: MEMBER_UPDATED, member });
    setMemberUpdated(true);
  };

  const allStates = useSelector((state) => state);
  const qtState = get(allStates, ["qt"], {});
  const config = get(qtState, ["config"]);
  const cdnImage = get(config, ["cdn-image"], "");
  const collection = get(qtState, ["data", "collection"], {});
  const pageType = qtState.pageType;
  const initialStory = get(qtState, ["data", "story"], {});
  const [activeStory, setActiveStory] = useState(null);
  const [isActivePerpetualStory, setIsActivePerpetualStory] = useState(false);
  const [hasLoadedPerpetualStories, setHasLoadedPerpetualStories] = useState(false);
  const [isNearStoryTop, setIsNearStoryTop] = useState(true);
  const story = pageType === "story-page" ? activeStory || initialStory : initialStory;
  const creatorShareTitle = getCreatorSocialTitle(story);
  const { horizontalDefault } = get(qtState, ["data", "navigationMenu"], {});
  const isWebView = get(qtState, ["config", "isWebView"]) === true;

  const storyHeaderInView = get(allStates, "storyHeaderInView", true);
  const iconSize = { width: "18px", height: "18px" };
  const { summaryPresent, showSummary, setShowSummary, summaryIndex } = useSummaryCheck(story?.cards);
  const [aiHubClick, setAiHubClick] = useState(false);
  const storyTemplate = get(story, ["story-template"], "");
  const [shareURL, setShareURL] = useState("");
  const readEmagazineLink = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "read_emagazine_link"], "")
  );
  const faqUrl = get(config, ["publisher-attributes", "publisher-faq-url"], false);
  const storyId = getAiFaqStoryId(story);
  const {
    isTextStory,
    isMagazineStory,
    isAiSummaryEnabled,
    isAfterAiSummaryCutoff,
    shouldEnableAiHubForTextStory,
    canUseApiSummaryFallback,
  } = getAiSummaryEligibility({
    story,
    publisherConfig: get(config, ["publisher-attributes"], {}),
    summaryPresent,
  });
  const shouldFetchAiFaqData =
    faqUrl !== false &&
    ((isAiSummaryEnabled && isMagazineStory && isAfterAiSummaryCutoff) ||
      canUseApiSummaryFallback ||
      shouldEnableAiHubForTextStory);
  const {
    hasSummary,
    hasFaqs,
    hasTopic,
    hasKeyTakeaways,
    hasTimeline,
    hasGlossary,
    hasTweets,
    summary,
    headline: apiHeadline,
    heroImageS3Key: apiHeroImageS3Key,
    isLoading,
    hasInTheStory,
  } = useAiFaqData({
    faqUrl,
    storyId,
    enabled: pageType === "story-page" && shouldFetchAiFaqData,
  });
  const hasApiSummary = hasSummary && Boolean(summary);
  const shouldShowAiHubButton =
    shouldEnableAiHubForTextStory &&
    !isLoading &&
    (hasTopic || hasKeyTakeaways || hasTimeline || hasGlossary || hasTweets || hasInTheStory);
  // On article pages, the FAQ API is only the fallback when no authored CMS summary element exists.
  const shouldUseApiSummary = canUseApiSummaryFallback && hasApiSummary;
  const shouldShowSummary = summaryPresent || shouldUseApiSummary;
  const shouldRenderSummaryButton =
    pageType === "story-page" &&
    (isMagazineStory || (isTextStory && !shouldEnableAiHubForTextStory)) &&
    (summaryPresent || canUseApiSummaryFallback);
  const isSummaryButtonEnabled = shouldShowSummary;
  const isSummaryButtonPending = !summaryPresent && canUseApiSummaryFallback && isLoading;

  const isSubscriptionPage = pageType === "subscription-page";
  const storyShareUrl = story?.url || getStoryUrl(story, story?.slug ? `/${story.slug}` : "") || shareURL;
  const shouldForceStickyHeadline =
    pageType === "story-page" &&
    (isActivePerpetualStory || (hasLoadedPerpetualStories && !isNearStoryTop));
  const shouldUseStickyHeader = !storyHeaderInView || shouldForceStickyHeadline;

  useEffect(() => {
    if (pageType !== "story-page") return;

    const handleActiveStoryChange = (event) => {
      if (event?.detail?.story) {
        setActiveStory(event.detail.story);
        setIsActivePerpetualStory(event.detail.isPerpetualStory === true);
      }
    };

    window.addEventListener("perpetual-story-active", handleActiveStoryChange);

    return () => {
      window.removeEventListener("perpetual-story-active", handleActiveStoryChange);
    };
  }, [pageType]);

  useEffect(() => {
    if (pageType !== "story-page") return;

    const handlePerpetualStoriesLoaded = () => {
      setHasLoadedPerpetualStories(true);
    };

    window.addEventListener("perpetual-stories-loaded", handlePerpetualStoriesLoaded);

    return () => {
      window.removeEventListener("perpetual-stories-loaded", handlePerpetualStoriesLoaded);
    };
  }, [pageType]);

  useEffect(() => {
    if (pageType !== "story-page") return;

    const updateNearStoryTop = () => {
      const nextIsNearStoryTop = window.scrollY <= STORY_TOP_MENU_THRESHOLD;
      setIsNearStoryTop((currentIsNearStoryTop) =>
        currentIsNearStoryTop === nextIsNearStoryTop ? currentIsNearStoryTop : nextIsNearStoryTop
      );
    };

    updateNearStoryTop();
    window.addEventListener("scroll", updateNearStoryTop, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateNearStoryTop);
    };
  }, [pageType]);

  useEffect(() => {
    setActiveStory(null);
    setIsActivePerpetualStory(false);
    setHasLoadedPerpetualStories(false);
    setIsNearStoryTop(true);
  }, [getStoryContentId(initialStory)]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const toastMessage = urlParams.get("toastMessage") || "";
    if (toastMessage) {
      setToastMessage(toastMessage);
      urlParams.delete("toastMessage");
      const newSearch = urlParams.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  // const handleAiHubClick = () => setAiHubClick(true);

  useEffect(() => {
    if (member) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "dl_login_success",
        login_status: "logged-in",
        user_id: `${member?.id}`,
        subscription_status: "not-subscribed",
      });
    }
  }, [member]);

  useEffect(() => {
    if (!isAdfree) {
      const header = document.getElementById("header");
      const isProfilePage = pageType === "company-page" || pageType === "individual-page";

      header?.classList.toggle("sticky-header-top", shouldUseStickyHeader);
      header?.classList.toggle("different-topbar-top", isProfilePage && shouldUseStickyHeader);
    }

    setShareURL(window.location.href);
  }, [isAdfree, pageType, shouldUseStickyHeader]);

  useEffect(() => {
    return () => {
      if (isAdfree) return;

      const header = document.getElementById("header");
      header?.classList.remove("different-topbar-top");
      header?.classList.remove("sticky-header-top");
    };
  }, [isAdfree]);

  useEffect(() => {
    if (showSummary || aiHubClick) {
      const scrollY = window.scrollY;

      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
      hasLockedBodyScrollRef.current = true;
    } else {
      if (!hasLockedBodyScrollRef.current) {
        return;
      }

      const scrollY = Math.abs(parseInt(document.body.style.top || "0", 10));

      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";

      window.scrollTo(0, scrollY);
      hasLockedBodyScrollRef.current = false;
    }
  }, [showSummary, aiHubClick]);

  useEffect(() => {
    if ((pageType === "section-page" || pageType === "collection-page" || pageType === "home-page") && config) {
      addDatalayerEventCollection(collection, config);
    }
  }, [collection]);

  useEffect(() => {
    const qtAuthCookie = getCookie("qt-auth");
    if (logoClick && member && qtAuthCookie) {
      window.location.href = window.location.origin + "/";
    }
  }, [logoClick]);

  useEffect(() => {
    if (memberUpdated) {
      setMemberUpdated(false);
      window.location.reload(window.location.href);
    }
  }, [memberUpdated]);

  const handleLogoClick = () => {
    setLogoClick(true);
    if (typeof window !== "undefined") {
      const categoryInfo = getCategoryAndSubCategory(window.location, pageType, config, story);
      const templateName = getTemplateName(pageType === "story-page" ? story?.["story-template"] : pageType);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "dl_menu_click",
        link_url: "/",
        link_text: "logo",
        link_category: "logo",
        category: categoryInfo.category,
        sub_category: categoryInfo.subCategory,
        content_id: categoryInfo.contentId,
        template_name: templateName,
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  const trackPopupSocialShareClick = (platform) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, config, story);

      let templateName;
      const isProfilePage = ["individual-page", "company-page"].includes(pageType);
      if (story?.["story-template"]) {
        templateName = getTemplateName(story?.["story-template"]);
      } else if (isProfilePage) {
        templateName = "profilepage";
      } else {
        templateName = getTemplateName(pageType);
      }
      window.dataLayer.push({
        event: "dl_story_interactions",
        event_action: "share_click",
        link_text: platform || "share",
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: templateName,
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };
  const hideListenButton = story["story-template"] === "live-blog" || story["story-template"] === "photo";

  const headlineOrMenuItems =
    pageType === "story-page" && shouldUseStickyHeader ? (
      <Headline type={2} customStyleName="ellipsis secondary-header-headline">
        {story?.headline || collection?.name}
      </Headline>
    ) : (
      <ul styleName="menu-items">
        {horizontalDefault?.map((menu, index) => {
          const { title, completeUrl, children } = menu;
          const hrefValue = ["link", "section", "tag"].includes(menu?.["item-type"]) ? completeUrl : "";
          return (
            <Fragment key={`${title}-${index}`}>
              {!children?.length ? (
                <HeaderMenuItem
                  isIconNeeded={false}
                  menuItemvalue={title}
                  href={completeUrl}
                  config={config}
                  story={story}
                />
              ) : (
                <li>
                  <MenuWithDropdown
                    subMenuitems={children}
                    menuItemvalue={title}
                    href={hrefValue}
                    config={config}
                    story={story}
                  />
                </li>
              )}
            </Fragment>
          );
        })}
      </ul>
    );

  const defaultOrSecondaryHeaderBtns = !shouldUseStickyHeader ? (
    <div styleName="btns">
      <AuthButton label={SIGN_IN} path="/user-login" customStyleName="" />
      {/* {enableSubscription && !isSubscriptionPage && !member && (
        <Button socialButton href="/subscription" customStyleName="bg-brand-primary">
          {GO_AD_FREE}
        </Button>
      )} */}
      {/* <Button socialButton customStyleName="bg-brand-primary" href="/apps">
          <i styleName="appIcon"/> App
        </Button> */}
    </div>
  ) : (
    <div styleName="secondary-header-btns-wrapper">
      <div styleName="secondary-header-btns">
        {story?.["story-template"] !== "creator" && <EntityListingStory story={story} config={config} isStoryHeader />}
        {BUTTONS_DATA.map((item, index) => {
          const {
            iconType = "share",
            iconHeight = iconSize.height,
            iconWidth = iconSize.width,
            customStyleName = "secondary-header-btn",
            text = "",
            ariaLabel,
          } = item;
          if (
            iconType === "play-solid" &&
            (story["story-template"] === "video" ||
              pageType === "author-page" ||
              pageType === "company-page" ||
              pageType === "individual-page" ||
              (story["story-template"] === "magazine" && !member) ||
              story["story-template"] === "sponsored")
          ) {
            return null;
          }
          if (!shouldRenderSummaryButton && !shouldShowAiHubButton && iconType === "summary") {
            return null;
          }
          if (iconType === "share") {
            return (
              <SocialShare
                popupText={pageType === "company-page" || pageType === "individual-page" ? "Share this URL" : ""}
                key={index}
                template={(props) => (
                  <SocialShareTemplate
                    {...props}
                    story={story}
                    shareTitle={creatorShareTitle}
                    qtConfig={config}
                    buttonLabel={"Share"}
                    onShareClick={(platform) => trackPopupSocialShareClick(platform?.toLowerCase())}
                  />
                )}
                buttonLabel={hideListenButton ? "Share" : ""}
                fullUrl={storyShareUrl ? encodeURI(storyShareUrl) : encodeURI(shareURL)}
                title={
                  creatorShareTitle ||
                  story.headline ||
                  collection?.name ||
                  qtState?.data?.individualInfo?.["display-name"] ||
                  qtState?.data?.companyInfo?.name ||
                  qtState?.data?.query ||
                  qtState?.data?.title ||
                  qtState?.data?.author?.name ||
                  qtState?.data?.top15Label ||
                  ""
                }
                shareIconId={`share-icon-story-share-${story.id}`}
              />
            );
          }

          if (pageType === "section-page" && index <= 1) {
            return null;
          }

          if (iconType === "summary") {
            if (shouldRenderSummaryButton) {
              const { category, subCategory, contentId } = getCategoryAndSubCategory(
                window.location,
                pageType,
                config,
                story
              );
              return (
                <span
                  key={`${index}-${iconType}`}
                  onClick={(e) => {
                    if (!isSummaryButtonEnabled) {
                      return;
                    }
                    // Push GA event
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                      event: "dl_story_interactions",
                      event_action: "summary_click",
                      category,
                      sub_category: subCategory,
                      content_id: contentId,
                      template_name: getTemplateName(story?.["story-template"] || pageType),
                      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
                    });
                    setShowSummary(true);
                  }}
                >
                  <Button
                    iconType={iconType}
                    iconHeight={iconHeight}
                    iconWidth={iconWidth}
                    customStyleName={customStyleName}
                    ariaLabel={isSummaryButtonPending ? "Summary is loading" : ariaLabel}
                  >
                    {text ? <span styleName="btn-text">{text}</span> : ""}
                  </Button>
                </span>
              );
            }

            // if (shouldShowAiHubButton) {
            //   const { category, subCategory, contentId } = getCategoryAndSubCategory(
            //     window.location,
            //     pageType,
            //     config,
            //     story
            //   );
            //   return (
            //     <span
            //       key={`${index}-${iconType}`}
            //       onClick={(e) => {
            //         // Push GA event
            //         window.dataLayer = window.dataLayer || [];
            //         window.dataLayer.push({
            //           event: "dl_story_interactions",
            //           event_action: "aihub_click",
            //           category,
            //           sub_category: subCategory,
            //           content_id: contentId,
            //           template_name: getTemplateName(pageType),
            //           platform: window.innerWidth >= 768 ? "desktop" : "mobile",
            //         });
            //         handleAiHubClick();
            //       }}
            //     >
            //       <button aria-label="Open AI Hub" styleName="aihubbutton"></button>
            //     </span>
            //   );
            // }

            return null;
          }

          return (
            <span key={`${index}-${iconType}`}>
              <Button
                iconType={iconType}
                iconHeight={iconHeight}
                iconWidth={iconWidth}
                customStyleName={customStyleName}
                ariaLabel={ariaLabel}
              >
                {text ? <span styleName="btn-text">{text}</span> : ""}
              </Button>
            </span>
          );
        })}
      </div>
      {/* <Button customStyleName="bg-brand-primary">{GO_AD_FREE}</Button> */}
    </div>
  );
  return (
    <React.Fragment>
      {showOneTap && !isWebView && <OneTapLogin setMember={setMember} member={member} />}

      <div className="topbar-wrapper" styleName={`topbar-wrapper ${shouldUseStickyHeader ? "moveUpDown" : ""}`}>
        <div styleName="topbar">
          <div styleName="left-wrapper">
            <div styleName="icons-wrapper">
              <div styleName="overlay" onClick={() => toggleHandler()} style={{ opacity: displayOverlay }}></div>
              {!isMobile && (
                <HamburgerMenu
                  onMenuToggle={() => toggleHandler()}
                  isMegaMenuOpen={isHamburgerMenuOpen}
                  hamburgerMenu={hamburgerMenu}
                />
              )}

              <div styleName="applogo-wrapper" onClick={handleLogoClick}>
                <AppLogo customStyle="app-logo-header" />
              </div>
            </div>
            {shouldUseStickyHeader && !isMobile && (pageType === "company-page" || pageType === "individual-page") && (
              <div styleName="company">
                {pageType === "company-page" && (
                  <span styleName="photo">
                    {qtState.data.companyInfo?.photo?.[0]?.key ? (
                      <img
                        styleName="image"
                        src={`https://${cdnImage}/${qtState.data.companyInfo?.photo?.[0]?.key}?w=128`}
                      />
                    ) : (
                      <CompanyIcon />
                    )}
                  </span>
                )}
                {pageType === "individual-page" && (
                  <span styleName="photo">
                    {qtState.data.individualInfo?.photo?.[0]?.key ? (
                      <img
                        styleName="image"
                        src={`https://${cdnImage}/${qtState.data.individualInfo?.photo?.[0]?.key}?w=128`}
                      />
                    ) : (
                      <UserFallbackIcon />
                    )}
                  </span>
                )}
                {pageType === "individual-page" ? (
                  <span styleName="name">
                    {qtState?.data?.individualInfo?.["display-name"] || qtState?.data?.individualInfo?.name}
                  </span>
                ) : (
                  <span styleName="name">{qtState?.data?.companyInfo?.name}</span>
                )}
              </div>
            )}
            {pageType === "company-page" || pageType === "individual-page"
              ? !shouldUseStickyHeader && headlineOrMenuItems
              : headlineOrMenuItems}
            {shouldUseStickyHeader && isMobile && (
              <div onClick={handleLogoClick}>
                <AppLogo customStyle="app-logo-header" />
              </div>
            )}
          </div>
          {!shouldUseStickyHeader && isMobile && (
            <div styleName="middle-wrapper" onClick={handleLogoClick}>
              <AppLogo customStyle="app-logo-header" />
            </div>
          )}
          {/* <div styleName="right-wrapper" style={!storyHeaderInView ? { gap: "var(--spacing-m)" } : {}}> */}
          <div styleName="right-wrapper">
            {!isMobile && (
              <div styleName={`btn ${!shouldUseStickyHeader ? "active" : ""}`}>
                <NavbarSearch />
              </div>
            )}
            {isMobile && pageType === "home-page" &&
                <a href="https://shop.yudirect.biz/FortuneIndia/Fortune.php" target="_blank" rel="noopener noreferrer" styleName="btn-get-print">Get Print</a>}
            {isMobile && !shouldUseStickyHeader && (
              <>
                <HamburgerMenu
                  onMenuToggle={() => toggleHandler()}
                  isMegaMenuOpen={isHamburgerMenuOpen}
                  hamburgerMenu={hamburgerMenu}
                />
              </>
            )}

            {defaultOrSecondaryHeaderBtns}
            {pageType === "home-page" &&
            <div styleName="btns">
              <a href="https://shop.yudirect.biz/FortuneIndia/Fortune.php" target="_blank" rel="noopener noreferrer" styleName="btn-get-print">Get Print</a>
            </div>
            }
          </div>
        </div>
        {shouldUseStickyHeader && isMobile && (pageType === "company-page" || pageType === "individual-page") && (
          <div styleName="company">
            {pageType === "company-page" && (
              <span styleName="photo">
                {qtState.data.companyInfo?.photo?.[0]?.key ? (
                  <img
                    styleName="image"
                    src={`https://${cdnImage}/${qtState.data.companyInfo?.photo?.[0]?.key}?w=128`}
                  />
                ) : (
                  <CompanyIcon />
                )}
              </span>
            )}
            {pageType === "individual-page" && (
              <span styleName="photo">
                {qtState.data.individualInfo?.photo?.[0]?.key ? (
                  <img
                    styleName="image"
                    src={`https://${cdnImage}/${qtState.data.individualInfo?.photo?.[0]?.key}?w=128`}
                  />
                ) : (
                  <UserFallbackIcon />
                )}
              </span>
            )}
            {pageType === "individual-page" ? (
              <span styleName="name">
                {qtState?.data?.individualInfo?.["display-name"] || qtState?.data?.individualInfo?.name}
              </span>
            ) : (
              <span styleName="name">{qtState?.data?.companyInfo?.name}</span>
            )}
          </div>
        )}
        {shouldUseStickyHeader && isMobile && pageType === "story-page" && story["story-template"] === "magazine" && (
          <Link href={readEmagazineLink} disableAjaxLinks={true}>
            <div styleName="read-e-magazine">
              Read Full E-Magazine
              {/* <RightArrow color="#C6554F" /> */}
            </div>
          </Link>
        )}
      </div>
      {shouldShowSummary && showSummary && (
        <Suspense fallback={<div></div>}>
          <SummaryCard
            setShowSummary={setShowSummary}
            story={story}
            summaryIndex={summaryIndex}
            summaryHtml={shouldUseApiSummary ? summary : ""}
            summaryTitle={shouldUseApiSummary ? apiHeadline : ""}
            summaryImage={shouldUseApiSummary ? apiHeroImageS3Key : ""}
          />
        </Suspense>
      )}
      {showSummary && <div styleName="blur-bg" />}
      {toastMessage && <ToastMessage message={toastMessage} onClose={() => setToastMessage("")} />}
    </React.Fragment>
  );
};

TopBar.propTypes = {
  showOneTap: bool,
  headline: string,
  publishTime: number,
};

export { TopBar };
